/* Le parcours d'une vente, de l'offre à l'encaissement.
 *
 *   devis → commande client → commande usine → expédié → livré → facturé → payé
 *
 * Deux commandes et non une : ce que le client commande à Omnix et ce qu'Omnix
 * commande à l'usine n'ont ni la même date, ni le même numéro, ni le même
 * destinataire. Les confondre, c'était perdre l'un des deux.
 *
 * Chaque étape ne s'ouvre qu'avec ce qui la justifie : un bon de commande a un
 * numéro, une expédition a une date. On ne facture pas sans la facture de
 * l'usine, parce que c'est elle qui porte la ligne de commission. */

import type { EtapeVente, Vente } from "@/types/metier";

export interface DefEtape {
  cle: EtapeVente;
  nom: string;
  /** Libellé court, pour le téléphone et les étiquettes. */
  court: string;
  ton: string;
}

/* Une couleur par étape, et non deux pour tout le parcours : on doit lire où
   en est une vente d'un coup d'œil dans une liste, sans compter les barres. */
export const ETAPES: readonly DefEtape[] = [
  { cle: "devis",           nom: "Devis",           court: "Devis",    ton: "ink-3" },
  { cle: "commande_client", nom: "Commande client", court: "Cde cli.", ton: "accent" },
  { cle: "commande_usine",  nom: "Commande usine",  court: "Cde us.",  ton: "violet" },
  { cle: "expedie",         nom: "Expédié",         court: "Expédié",  ton: "info" },
  { cle: "livre",           nom: "Livré",           court: "Livré",    ton: "ciel" },
  { cle: "facture",         nom: "Facturé",         court: "Facturé",  ton: "warn" },
  { cle: "paye",            nom: "Payé",            court: "Payé",     ton: "ok" },
];

export function etapeDe(cle: EtapeVente): DefEtape {
  return ETAPES.find((e) => e.cle === cle) ?? { cle, nom: cle, court: cle, ton: "" };
}

export function etapeSuivante(cle: EtapeVente): EtapeVente | null {
  const i = ETAPES.findIndex((e) => e.cle === cle);
  return ETAPES[i + 1]?.cle ?? null;
}

/**
 * Ce qui empêche la vente d'avancer, en une phrase, ou `null` si rien ne
 * l'empêche.
 */
/** Ce que chaque étape réclame avant de s'ouvrir. */
type Porte = { champ: keyof Vente; manque: string };

const PORTES: Partial<Record<EtapeVente, Porte>> = {
  commande_usine: { champ: "bdc_client",
    manque: "Passer en commande usine demande d'abord le bon de commande du client : c'est lui qui engage l'affaire." },
  expedie: { champ: "bdc_usine",
    manque: "Rien ne part sans commande passée à l'usine : renseignez son numéro et sa date." },
  livre: { champ: "expedition",
    manque: "Marquer livré demande la date d'expédition : on ne reçoit pas ce qui n'est pas parti." },
  facture: { champ: "facture_usine",
    manque: "Passer en facturé demande le numéro de la facture de l'usine : c'est lui qui porte la ligne de commission." },
};

export function blocage(
  vente: Pick<Vente, "etape" | "facture_usine" | "bdc_client" | "bdc_usine" | "expedition">,
): string | null {
  const suivante = etapeSuivante(vente.etape);
  if (!suivante) return "La vente est au terme de son parcours.";
  const porte = PORTES[suivante];
  if (porte && !vente[porte.champ as keyof typeof vente]) return porte.manque;
  return null;
}

/** Une vente porte une commission dès la commande du client, pas au devis :
    c'est là que l'affaire est faite, pas quand on a proposé un prix. */
export function porteCommission(etape: EtapeVente): boolean {
  return etape !== "devis";
}

/** Total des lignes, qui peut s'écarter du montant saisi sur la vente. */
export function totalLignes(vente: Pick<Vente, "lignes">): number {
  return Math.round((vente.lignes ?? []).reduce((s, l) => s + Number(l.total || 0), 0) * 100) / 100;
}
